import {
  Badge,
  Box,
  Center,
  Flex,
  Icon,
  Stack,
  useMediaQuery,
  VStack,
} from '@chakra-ui/react';
import { Divider, Text } from '@diamond/shared/ui';
import {
  DateFormatterIdn,
  getStatusMap,
  LIST_FEATURE_FLAGS,
  useFeatureFlag,
} from '@diamond/shared/utils';
import { useShipToStore } from '@diamond/sol-buyer-context';
import { DateRangeOutlined } from '@mui/icons-material';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import shallow from 'zustand/shallow';

import {
  MyOrderDetailCardProps,
  OrderSummary,
  PaymentActions,
  ProductCounter,
  ProductPreview,
  StateOptionsStyle,
  UpdateNotification,
} from './card';

export function MyOrderDetailCard({ data }: MyOrderDetailCardProps) {
  const [isMobile] = useMediaQuery('(max-width: 768px)');
  const [state, setState] = useState<string>('');
  const FF_ADMIN_PO_EFFECTIVE_DATE = useFeatureFlag(
    LIST_FEATURE_FLAGS.FF_ADMIN_PO_EFFECTIVE_DATE,
    false
  );
  const [stateOption, setStateOptions] = useState<StateOptionsStyle>({
    color: 'blue',
    bg: 'rgba(0, 0, 163, 0.1)',
  });

  const [selectedShipToId] = useShipToStore(
    (s) => [s.selectedShipToId],
    shallow
  );

  useEffect(() => {
    if (data.status) {
      const { state, stateOptions } = getStatusMap(
        data.status,
        data.reason,
        data.sap_note
      );
      setState(state);
      setStateOptions(stateOptions);
    } else {
      setState('Draft');
      setStateOptions({ color: 'blue', bg: 'rgba(0, 0, 163, 0.1)' });
    }
  }, [data]);

  const showInvoiceButton =
    data.invoice_link &&
    (data.status === 'received' || data.status === 'partially_received');
  const defaultDate = format(
    data?.transaction_date,
    'eeee, d MMMM yyyy • HH:mm',
    {
      locale: id,
    }
  );

  return (
    <Box
      id={data.status || 'order-card'}
      rounded="lg"
      border="1px"
      borderColor="gray.33"
      shadow="base"
      p="4"
    >
      <Flex
        alignItems="center"
        justifyContent="space-between"
        gap={{ base: '1', md: '4' }}
        mb="2"
      >
        <Box display={{ base: 'none', md: 'flex' }} gap="2">
          <Icon as={DateRangeOutlined} color="blue" />
          <Text fontWeight="semibold" fontSize="md">
            {FF_ADMIN_PO_EFFECTIVE_DATE
              ? DateFormatterIdn(data.po_effective_date)
              : defaultDate}
          </Text>
        </Box>
        <Stack display={{ base: 'block', md: 'none' }} w="full" spacing="2">
          <Flex alignItems="center" gap="1">
            <Icon as={DateRangeOutlined} color="blue" boxSize="20px" />
            <Text fontSize="2xs">
              {format(data?.transaction_date, 'eeee, d MMMM yyyy • HH:mm', {
                locale: id,
              })}
            </Text>
          </Flex>
          <Divider />
        </Stack>
        <Flex
          gap="8"
          alignItems="center"
          display={{ base: 'none', md: 'flex' }}
        >
          <VStack align="flex-start" spacing="0" minW="fit-content">
            <Text fontSize="xs" textColor="gray.75">
              No. PO
            </Text>
            <Text fontWeight="semibold" color="green">
              {data?.invoice_link ? (
                <Link
                  href={data?.invoice_link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {data?.transaction_id}
                </Link>
              ) : (
                data?.transaction_id
              )}
            </Text>
          </VStack>
          <VStack align="flex-start" spacing="0" minW="fit-content">
            <Text fontSize="xs" textColor="gray.75" minW="fit-content">
              No. Sub PO
            </Text>
            <Text fontWeight="semibold" color="blue">
              {data?.sub_po_number}
            </Text>
          </VStack>
          {data.delivery_link && data.status === 'out_for_delivery' ? (
            <Link
              href={data.delivery_link}
              target="_blank"
              rel="noopener noreferrer"
              style={{ width: '100%' }}
            >
              <Badge
                fontSize="14px"
                rounded="lg"
                px="2"
                py="1"
                {...stateOption}
              >
                {state}
              </Badge>
            </Link>
          ) : (
            <Badge fontSize="14px" rounded="lg" px="2" py="1" {...stateOption}>
              {state}
            </Badge>
          )}
        </Flex>
      </Flex>
      <Box display={{ base: 'contents', md: 'none' }}>
        <Flex gap="2">
          <Text fontSize="xs" fontWeight="semibold" color="green">
            <Text as="span" fontSize="2xs" color="gray.75">
              PO{' '}
            </Text>
            {data?.sub_po_number}
          </Text>
        </Flex>
        <Center my="2">
          {data.delivery_link && data.status === 'out_for_delivery' ? (
            <Link
              href={data.delivery_link}
              target="_blank"
              rel="noopener noreferrer"
              style={{ width: '100%' }}
            >
              <Badge
                fontSize="xs"
                rounded="lg"
                px="2"
                py="1"
                w="full"
                textAlign="center"
                {...stateOption}
              >
                {state}
              </Badge>
            </Link>
          ) : (
            <Badge
              fontSize="xs"
              rounded="lg"
              px="2"
              py="1"
              w="full"
              textAlign="center"
              {...stateOption}
            >
              {state}
            </Badge>
          )}
        </Center>
        <PaymentActions
          data={data}
          isMobile
          selectedShipToId={selectedShipToId}
        />
      </Box>
      <Divider display={{ base: 'none', md: 'block' }} variant="dashed" />
      <Box w={{ base: 'full', md: '40%' }}>
        <ProductPreview data={data} isMobile={isMobile} />
        <ProductCounter itemsLength={data.items.length} />
      </Box>
      <UpdateNotification
        updatedItemsCount={data.updated_items_count}
        updatedItemsString={data.updated_items_string}
      />

      <OrderSummary
        data={data}
        selectedShipToId={selectedShipToId}
        isMobile={isMobile}
      />
    </Box>
  );
}
